import React from 'react';
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import styled from '@emotion/styled';
import {
  useTaxDeliveryKba,
  useUpdateTaxDeliveryKba,
  useReminderType,
  useUpdateReminderType,
} from 'hooks/tax-delivery-settings-hooks';
import {
  REMINDER_TYPE_OPTIONS,
  ReminderType,
} from 'api/tax-delivery-settings';

const Info = styled.div`
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;
  font-size: 0.95em;
  line-height: normal;
`;

export default function TaxDeliverySettings() {
  const { data: kbaSetting, isLoading: kbaLoading } =
    useTaxDeliveryKba();
  const { data: reminderType, isLoading: reminderLoading } =
    useReminderType();
  const updateKba = useUpdateTaxDeliveryKba();
  const updateReminderType = useUpdateReminderType();

  const isLoading = kbaLoading || reminderLoading;

  const handleToggleKba = () => {
    updateKba.mutate(!kbaSetting);
  };

  const handleReminderTypeChange = (event: SelectChangeEvent) => {
    updateReminderType.mutate(event.target.value as ReminderType);
  };

  const selectedReminder = REMINDER_TYPE_OPTIONS.find(
    (opt) => opt.value === (reminderType ?? 'remind_to_pay'),
  );

  return (
    <>
      {isLoading ? (
        <div
          id="loading"
          role="status"
          aria-label="Loading TaxDeliverySettings"
        />
      ) : null}

      <div className="center-wrapper">
        <h2>Tax Delivery</h2>
        <div style={{ color: '#7d7d7e' }}>
          Configure default settings for Tax Delivery requests. These settings
          apply as defaults when creating new tax deliveries and can be
          overridden per request.
        </div>

        <div className="mfa-card mt-4">
          <Typography fontWeight={700} variant="body1" gutterBottom>
            KBA for Tax Delivery
          </Typography>

          <div className="d-flex align-items-center mb-3">
            <div className="toggle-container mr-2">
              <input
                type="checkbox"
                className="toggle"
                checked={!!kbaSetting}
                onChange={handleToggleKba}
                aria-label="Toggle default KBA for tax delivery"
              />
            </div>
            <div className="mfa-title">
              Require Knowledge-Based Authentication (KBA)
            </div>
            {kbaSetting && <div className="green-badge ml-auto">ENABLED</div>}
          </div>
          <Info style={{ backgroundColor: '#F5F3B4' }}>
            {kbaSetting
            ? 'KBA will be required by default for all new tax delivery e-signature requests. This can be overridden per request during dispatch.'
            : 'When enabled, all new tax delivery e-signature requests will require Knowledge-Based Authentication by default. This adds an extra layer of identity verification for signers.'}
        </Info>

          <Typography className="mt-5" fontWeight={700} variant="body1" gutterBottom>
            Estimated Tax Payment Reminders
          </Typography>
          <div style={{ marginBottom: '10px' }}>
            <Select
              value={reminderType ?? 'remind_to_pay'}
              onChange={handleReminderTypeChange}
              fullWidth
              size="small"
              inputProps={{ 'aria-label': 'Estimated tax payment reminder type' }}
            >
              {REMINDER_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
          {selectedReminder && (
            <Info style={{ backgroundColor: '#F5F3B4' }}>
              {selectedReminder.description}
            </Info>
          )}
        </div>

      </div>
    </>
  );
}
